<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useCategoryStore } from "@/stores/useCategoryStore";
import { useUserStore } from "@/stores/useUserStore";

import { toTitleCase } from "@/utils/stringUtils";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";

// Componentes modais
import AddCategoryForm from "./AddCategoryForm.vue";
import EditCategoryForm from "./EditCategoryForm.vue";
import DeleteCategoryForm from "./DeleteCategoryForm.vue";
import CategoryCardModal from "./CategoryCardModal.vue";
import BatchDeleteCategoryForm from "./BatchDeleteCategoryForm.vue";
import SearchCategoryInput from "./SearchCategoryInput.vue";
import CategoryHelpModal from "./CategoryHelpModal.vue";

// Componentes de paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

// Store
const categoryStore = useCategoryStore();
const userStore = useUserStore();

// Estados de exibição dos modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);

const showCardModal = ref(false);
const selectedCategoryId = ref(null);

const showHelpModal = ref(false);

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);

// ✅ EVITA "FLASH" DE LISTA COMPLETA (renderiza somente a página após fetch paginado)
const categoriesToRender = ref([]);
const isTableReady = ref(false);
const isLoadingTable = ref(false);

// Lógica de Dropdowns e Seleção
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// ====================================================================
// ✅ TOAST PADRÃO
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

function resetToastTimeout(duration) {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, duration);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(6000);
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(3000);
}

function handleChildNotify(payload) {
  if (!payload) return;

  if (payload.type === "success") {
    showSuccessToast(payload.message || "Operação realizada com sucesso.");
    return;
  }

  showErrorToast(payload.message || "Ocorreu um erro.");
}

// ====================================================================
// ✅ REGRA SYS / VISUAL
// ====================================================================
function normalizeUserName(name) {
  return (name ?? "").toString().trim().toLowerCase();
}

function isLoggedSys() {
  return normalizeUserName(userStore.currentUser?.userName) === "sys";
}

function isCreatedBySys(category) {
  return normalizeUserName(category?.createdByUserName) === "sys";
}

function isRowSelectable(category) {
  const logged = normalizeUserName(userStore.currentUser?.userName);
  if (!logged) return false;

  if (logged === "sys") return true;

  const creator = normalizeUserName(category?.createdByUserName);
  if (!creator) return true;

  return creator !== "sys";
}

function sysProtectedTooltip(category) {
  if (!category) return "";
  if (isLoggedSys()) return "";
  if (!isCreatedBySys(category)) return "";
  return "Protegido (criado pelo Sys)";
}

function canManage(category) {
  const logged = normalizeUserName(userStore.currentUser?.userName);
  if (!logged) return false;

  if (logged === "sys") return true;

  const creator = normalizeUserName(category?.createdByUserName);
  if (!creator) return true;

  return creator !== "sys";
}

const isAllSelectedOnPage = computed({
  get: () => {
    const selectableOnPage = categoriesToRender.value.filter((c) =>
      isRowSelectable(c)
    );
    if (selectableOnPage.length === 0) return false;

    return selectableOnPage.every((c) =>
      categoryStore.selectedCategoryIds.includes(c.categoryId)
    );
  },
  set: (value) => {
    const selectableIdsOnPage = categoriesToRender.value
      .filter((c) => isRowSelectable(c))
      .map((c) => c.categoryId);

    const allIdsOnPage = categoriesToRender.value.map((c) => c.categoryId);

    if (value) {
      selectableIdsOnPage.forEach((id) => {
        if (!categoryStore.selectedCategoryIds.includes(id)) {
          categoryStore.selectedCategoryIds.push(id);
        }
      });
    } else {
      categoryStore.selectedCategoryIds =
        categoryStore.selectedCategoryIds.filter(
          (id) => !allIdsOnPage.includes(id)
        );
    }
  },
});

const anySelectableOnPage = computed(() =>
  categoriesToRender.value.some((c) => isRowSelectable(c))
);

const canBatchActions = computed(() => {
  return (
    Array.isArray(categoryStore.selectedCategoryIds) &&
    categoryStore.selectedCategoryIds.length > 0
  );
});

function toggleRowDropdown(categoryId) {
  openDropdownId.value =
    openDropdownId.value === categoryId ? null : categoryId;
}

function handleEdit(index) {
  indexSelector(index);
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(index) {
  handleOpenCard(index);
  openDropdownId.value = null;
}

function handleOpenCard(index) {
  const category = categoriesToRender.value[index];
  if (!category) return;

  selectedCategoryId.value = category.categoryId;
  categoryStore.setIdCategory(category.categoryId);
  showCardModal.value = true;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function toggleSelection(category) {
  if (!isRowSelectable(category)) return;
  categoryStore.toggleSelection(category.categoryId);
}

function handleBatchDelete() {
  isBatchDropdownOpen.value = false;

  const selectedIds = categoryStore.selectedCategoryIds || [];

  if (selectedIds.length === 0) {
    showErrorToast("Nenhum gênero foi selecionado para exclusão em lote.");
    return;
  }

  showBatchDeleteModal.value = true;
}

async function handleCategoryAdded() {
  if (currentPage.value !== 1) currentPage.value = 1;
  await fetchCategoriesPaged();
}

async function handleCategoryChanged() {
  await fetchCategoriesPaged();
}

async function handleCategoryDeleted() {
  showDeleteModal.value = false;
  showBatchDeleteModal.value = false;
  categoryStore.selectedCategoryIds = [];
  await fetchCategoriesPaged();
}

// Fetch de dados (paginado)
async function fetchCategoriesPaged() {
  try {
    isLoadingTable.value = true;

    if (!isTableReady.value) categoriesToRender.value = [];

    await categoryStore.fetchCategoriesPaged(
      currentPage.value,
      pageSize.value,
      categoryStore.searchTerm
    );

    categoriesToRender.value = Array.isArray(categoryStore.categories)
      ? [...categoryStore.categories]
      : [];

    isTableReady.value = true;

    if (!isLoggedSys() && categoriesToRender.value.length) {
      const sysIdsOnPage = categoriesToRender.value
        .filter((c) => isCreatedBySys(c))
        .map((c) => c.categoryId);

      if (sysIdsOnPage.length) {
        categoryStore.selectedCategoryIds =
          categoryStore.selectedCategoryIds.filter(
            (id) => !sysIdsOnPage.includes(id)
          );
      }
    }

    if (categoryStore.shouldForcePageOne) {
      categoryStore.shouldForcePageOne = false;

      if (currentPage.value !== 1) {
        currentPage.value = 1;
      } else {
        await categoryStore.fetchCategoriesPaged(
          1,
          pageSize.value,
          categoryStore.searchTerm
        );
        categoriesToRender.value = Array.isArray(categoryStore.categories)
          ? [...categoryStore.categories]
          : [];

        if (!isLoggedSys() && categoriesToRender.value.length) {
          const sysIdsOnPage = categoriesToRender.value
            .filter((c) => isCreatedBySys(c))
            .map((c) => c.categoryId);

          if (sysIdsOnPage.length) {
            categoryStore.selectedCategoryIds =
              categoryStore.selectedCategoryIds.filter(
                (id) => !sysIdsOnPage.includes(id)
              );
          }
        }
      }
    }
  } catch (error) {
    console.error(
      "❌ Erro ao buscar categorias paginadas:",
      error?.message || error
    );
    showErrorToast("Erro ao carregar gêneros.");
  } finally {
    isLoadingTable.value = false;
  }
}

watch(currentPage, fetchCategoriesPaged);

watch(
  () => categoryStore.searchTerm,
  () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
    } else {
      fetchCategoriesPaged();
    }
  }
);

watch(
  () => categoryStore.categories,
  (val) => {
    if (!isTableReady.value) return;
    categoriesToRender.value = Array.isArray(val) ? [...val] : [];

    if (!isLoggedSys() && categoriesToRender.value.length) {
      const sysIdsOnPage = categoriesToRender.value
        .filter((c) => isCreatedBySys(c))
        .map((c) => c.categoryId);

      if (sysIdsOnPage.length) {
        categoryStore.selectedCategoryIds =
          categoryStore.selectedCategoryIds.filter(
            (id) => !sysIdsOnPage.includes(id)
          );
      }
    }
  }
);

function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  fetchCategoriesPaged();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);

  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);

  categoryStore.searchTerm = "";
  categoryStore.currentPage = 1;
  categoryStore.shouldForcePageOne = true;

  categoryStore.selectedCategoryIds = [];
  openDropdownId.value = null;
  isBatchDropdownOpen.value = false;
  selectedCategoryId.value = null;

  categoriesToRender.value = [];
  isTableReady.value = false;
  isLoadingTable.value = false;
});

function indexSelector(index) {
  const category = categoriesToRender.value[index];
  if (!category) return;
  categoryStore.setIdCategory(category.categoryId);
}
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Gêneros</div>
          <div class="text-sm mt-1 text-gray-700 ">
            Gerencie os gêneros (conto, crônica, etc.) utilizados para
            classificar os textos.
            <button
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              @click="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <div class="action-bar">
        <div class="w-full md:w-1/2">
          <SearchCategoryInput />
        </div>
        <div
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
        >
          <button @click="showAddModal = true" class="btn btn-primary">
            <svg class="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              />
            </svg>
            Adicionar Gênero
          </button>

          <div ref="batchDropdownRef" class="relative">
            <button
              @click="isBatchDropdownOpen = !isBatchDropdownOpen"
              class="btn btn-batch w-full"
              :disabled="!canBatchActions"
            >
              Ações em lote
              <svg
                class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                :class="{ open: isBatchDropdownOpen }"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>

            <div v-if="isBatchDropdownOpen" class="dropdown-menu">
              <ul class="p-0 m-0 list-none"></ul>
              <div class="py-1">
                <a
                  href="#"
                  role="button"
                  @click.prevent="handleBatchDelete"
                  class="dropdown-item danger-item"
                >
                  <i class="fas fa-trash-alt"></i>
                  <span>Apagar selecionados</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anySelectableOnPage"
                  :class="{ 'disabled-item': !anySelectableOnPage }"
                />
              </th>
              <th style="width: 5%">Ações</th>
              <th style="width: 5%">Id</th>
              <th style="width: 20%">Gênero</th>
              <th style="width: 30%">Descrição</th>
              <th style="width: 15%">Criador</th>
              <th style="width: 15%">Data Atualização</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(category, index) in !isLoadingTable ? categoriesToRender : []"
              :key="category.categoryId"
              @click="handleOpenCard(index)"
              class="clickable-row"
            >
              <td
                @click.stop
                :class="{
 'checkbox-cell-disabled': !isRowSelectable(category),
                }"
              >
                <input
                  type="checkbox"
                  :value="category.categoryId"
                  :checked="
                    categoryStore.selectedCategoryIds.includes(
                      category.categoryId
                    )
                  "
                  :disabled="!isRowSelectable(category)"
                  :title="sysProtectedTooltip(category)"
                  :class="[
 'checkbox-style',
                    { 'checkbox-disabled': !isRowSelectable(category) },
                  ]"
                  @change="toggleSelection(category)"
                />
              </td>

              <td @click.stop class="action-cell-container">
                <button
                  @click="toggleRowDropdown(category.categoryId)"
                  class="btn-icon btn-soft-brand"
                  title="Menu de Ações"
                >
                  <i class="fas fa-ellipsis-h fa-lg"></i>
                </button>

                <div
                  v-if="openDropdownId === category.categoryId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li
                      :class="{ 'disabled-item': !canManage(category) }"
                      @click="canManage(category) && handleEdit(index)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>

                    <li @click="handleView(index)">
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>

                    <li
                      class="danger-item"
                      :class="{ 'disabled-item': !canManage(category) }"
                      @click="canManage(category) && handleDelete(index)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span :title="category.categoryId">{{
                  category.categoryId
                }}</span>
              </td>

              <td class="cell-text-left">
                <div class="flex items-center gap-2 min-w-0">
                  <span
                    class="truncate"
                    :title="toTitleCase(category.categoryName)"
                  >
                    {{ toTitleCase(category.categoryName) }}
                  </span>
                </div>
              </td>

              <td class="cell-text-left">
                <span :title="category.description">
                  {{ category.description }}
                </span>
              </td>

              <td class="cell-creator">
                <div
                  class="creator-cell"
                  :title="toTitleCase(category.createdByUserName)"
                >
                  <template v-if="isCreatedBySys(category)">
                    <span class="sys-badge" title="Criado pelo Sys">SYS</span>
                  </template>
                  <template v-else>
                    <span class="truncate">
                      {{ toTitleCase(category.createdByUserName) }}
                    </span>
                  </template>
                </div>
              </td>

              <td>
                <span :title="formatDateTimeForDisplay(category.updatedAt)">
                  {{ formatDateTimeForDisplay(category.updatedAt) }}
                </span>
              </td>
            </tr>

            <tr v-if="isLoadingTable" class="empty-row-state">
              <td colspan="7" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando gêneros...</p>
                </div>
              </td>
            </tr>

            <tr
              v-else-if="categoriesToRender.length === 0"
              class="empty-row-state"
            >
              <td colspan="7" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                  <p>Nenhum gênero encontrado.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!isLoadingTable && (categoryStore.totalCount ?? 0) > 0"
          class="category-pagination-row"
        >
          <div class="category-pagination-left">
            <span class="category-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="categoryStore.totalCount ?? 0"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="categoryStore.totalCount ?? 0"
            :total-pages="categoryStore.totalPages ?? 0"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <AddCategoryForm
      v-if="showAddModal"
      @close="showAddModal = false"
      @category-added="handleCategoryAdded"
    />
    <EditCategoryForm
      v-if="showEditModal"
      @close="showEditModal = false"
      @refresh="handleCategoryChanged"
      @notify="handleChildNotify"
    />
    <CategoryCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :category-id="selectedCategoryId"
      @close="showCardModal = false"
      @category-updated="handleCategoryChanged"
      @notify="handleChildNotify"
    />
    <DeleteCategoryForm
      v-if="showDeleteModal"
      @close="showDeleteModal = false"
      @refresh="handleCategoryDeleted"
      @notify="handleChildNotify"
    />
    <BatchDeleteCategoryForm
      v-if="showBatchDeleteModal"
      @close="showBatchDeleteModal = false"
      @refresh="handleCategoryDeleted"
      @notify="handleChildNotify"
    />
    <CategoryHelpModal :show="showHelpModal" @close="showHelpModal = false" />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

.btn-pastel-green {
  background-color: #9bf6ff;
  color: #1a4a4f;
}

.row-dropdown-menu li.dropdown-item-disabled:hover {
  transform: none;
  box-shadow: none;
}

.checkbox-cell-disabled * {
  cursor: not-allowed !important;
}

.action-bar input,
.action-bar .search-input,
.search-text-input input {
  font-size: 0.8rem !important;
}

.cell-creator {
  text-align: center;
}
.cell-creator > .creator-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: none;
  margin: 0;
}
.cell-creator > .creator-cell .truncate {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.82rem;
}
.category-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.category-pagination-label {
  padding-top: 2px;
}

.empty-state-content {
  width: 100%;
  margin: 0 auto;
}

</style>